<script setup>
defineProps({
  showUndo: {
    type: Boolean,
    default: true,
  },
  showRedo: {
    type: Boolean,
    default: true,
  },
  showSubscript: {
    type: Boolean,
    default: false,
  },
  showSuperscript: {
    type: Boolean,
    default: false,
  },
  showH1: {
    type: Boolean,
    default: false,
  },
  showH2: {
    type: Boolean,
    default: false,
  },
  showBlockquote: {
    type: Boolean,
    default: false,
  },
  toolbarClass: {
    type: String,
    default: 'view-tray-modal__editor-toolbar',
  },
  buttonClass: {
    type: String,
    default: 'view-tray-modal__toolbar-btn',
  },
  boldActive: {
    type: Boolean,
    default: false,
  },
  italicActive: {
    type: Boolean,
    default: false,
  },
  subscriptActive: {
    type: Boolean,
    default: false,
  },
  superscriptActive: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['command', 'link', 'format-block']);

const execCommand = command => {
  emit('command', command);
};

const handleLink = () => {
  emit('link');
};

const formatBlock = tag => {
  emit('format-block', tag);
};
</script>

<template>
  <div :class="toolbarClass">
    <button
      v-if="showUndo"
      type="button"
      :class="buttonClass"
      title="Undo"
      aria-label="Undo"
      @click="execCommand('undo')"
    >
      <svg
        width="18"
        height="8"
        viewBox="0 0 18 8"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M8.75 0.833333C6.54167 0.833333 4.54167 1.65833 3 3L0 0V7.5H7.5L4.48333 4.48333C5.64167 3.51667 7.11667 2.91667 8.75 2.91667C11.7 2.91667 14.2083 4.84167 15.0833 7.5L17.0583 6.85C15.9 3.35833 12.625 0.833333 8.75 0.833333Z"
          fill="currentColor"
        />
      </svg>
    </button>
    <button
      v-if="showRedo"
      type="button"
      :class="buttonClass"
      title="Redo"
      aria-label="Redo"
      @click="execCommand('redo')"
    >
      <svg
        width="18"
        height="8"
        viewBox="0 0 18 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M14.05 3C12.5083 1.65833 10.5083 0.833333 8.3 0.833333C4.425 0.833333 1.15 3.35833 0 6.85L1.96667 7.5C2.40557 6.16666 3.25405 5.00583 4.39122 4.18287C5.5284 3.35991 6.89628 2.91681 8.3 2.91667C9.925 2.91667 11.4083 3.51667 12.5667 4.48333L9.55 7.5H17.05V0L14.05 3Z"
          fill="currentColor"
        />
      </svg>
    </button>
    <button
      type="button"
      :class="buttonClass"
      title="Bold"
      aria-label="Bold"
      :aria-pressed="boldActive"
      @click="execCommand('bold')"
    >
      <svg
        viewBox="0 0 24 24"
        height="20"
        width="20"
        aria-hidden="true"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z"
        />
      </svg>
    </button>
    <button
      type="button"
      :class="buttonClass"
      title="Italic"
      aria-label="Italic"
      :aria-pressed="italicActive"
      @click="execCommand('italic')"
    >
      <svg
        viewBox="0 0 24 24"
        height="20"
        width="20"
        aria-hidden="true"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z" />
      </svg>
    </button>
    <button
      v-if="showSubscript"
      type="button"
      :class="buttonClass"
      title="Subscript"
      aria-label="Subscript"
      :aria-pressed="subscriptActive"
      @click="execCommand('subscript')"
    >
      <svg
        viewBox="0 0 24 24"
        height="20"
        width="20"
        aria-hidden="true"
        focusable="false"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M22 18h-2v1h3v1h-4v-2c0-.55.45-1 1-1h2v-1h-3v-1h3c.55 0 1 .45 1 1v1c0 .55-.45 1-1 1zM5.88 18h2.66l3.4-5.42h.12l3.4 5.42h2.66l-4.65-7.27L17.81 4h-2.68l-3.07 4.99h-.12L8.85 4H6.19l4.32 6.73L5.88 18z"
        ></path>
      </svg>
    </button>
    <button
      v-if="showSuperscript"
      type="button"
      :class="buttonClass"
      title="Superscript"
      aria-label="Superscript"
      :aria-pressed="superscriptActive"
      @click="execCommand('superscript')"
    >
      <svg
        viewBox="0 0 24 24"
        height="20"
        width="20"
        aria-hidden="true"
        focusable="false"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M22 7h-2v1h3v1h-4V7c0-.55.45-1 1-1h2V5h-3V4h3c.55 0 1 .45 1 1v1c0 .55-.45 1-1 1zM5.88 20h2.66l3.4-5.42h.12l3.4 5.42h2.66l-4.65-7.27L17.81 6h-2.68l-3.07 4.99h-.12L8.85 6H6.19l4.32 6.73L5.88 20z"
        ></path>
      </svg>
    </button>
    <button
      v-if="showH1"
      type="button"
      :class="buttonClass"
      title="Heading 1"
      aria-label="Heading 1"
      @click="formatBlock('h1')"
    >
      <span aria-hidden="true">H1</span>
    </button>
    <button
      v-if="showH2"
      type="button"
      :class="buttonClass"
      title="Heading 2"
      aria-label="Heading 2"
      @click="formatBlock('h2')"
    >
      <span aria-hidden="true">H2</span>
    </button>
    <button
      type="button"
      :class="buttonClass"
      title="Insert Link"
      aria-label="Insert Link"
      @click="handleLink"
    >
      <svg
        viewBox="0 0 24 24"
        height="20"
        width="20"
        aria-hidden="true"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"
        />
      </svg>
    </button>
    <button
      type="button"
      :class="buttonClass"
      title="Bulleted List"
      aria-label="Bulleted List"
      @click="execCommand('insertUnorderedList')"
    >
      <svg
        viewBox="0 0 24 24"
        height="20"
        width="20"
        aria-hidden="true"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z"
        />
      </svg>
    </button>
    <button
      type="button"
      :class="buttonClass"
      title="Numbered List"
      aria-label="Numbered List"
      @click="execCommand('insertOrderedList')"
    >
      <svg
        viewBox="0 0 24 24"
        height="20"
        width="20"
        aria-hidden="true"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z"
        />
      </svg>
    </button>
    <button
      v-if="showBlockquote"
      type="button"
      :class="buttonClass"
      title="Block Quote"
      aria-label="Block Quote"
      @click="formatBlock('blockquote')"
    >
      <svg
        viewBox="0 0 24 24"
        height="20"
        width="20"
        aria-hidden="true"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z" />
      </svg>
    </button>
  </div>
</template>
